import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { adminHttp, formatApiError } from "../../lib/adminApi";
import { useAuth } from "../AuthContext";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminHttp.get("/admin/users");
      setUsers(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setShowDialog(true); };
  const openEdit = (u) => { setEditing(u); setShowDialog(true); };

  const remove = async (u) => {
    if (u.id === me.id) return toast.error("No puedes borrarte a ti mismo.");
    if (!window.confirm(`¿Eliminar al usuario ${u.email}?`)) return;
    try {
      await adminHttp.delete(`/admin/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="divider-accent" /><span className="kicker">Gestión</span>
          </div>
          <h1 className="font-display font-black tracking-tightest text-3xl md:text-5xl">Usuarios</h1>
        </div>
        <button onClick={openCreate} className="btn-accent py-3 px-5 text-xs" data-testid="users-create-btn">
          <Plus className="w-4 h-4" /> Nuevo usuario
        </button>
      </div>

      <div className="bg-white border border-[#DDE2D5] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#F2F4ED] text-left font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Rol</th>
              <th className="px-4 py-3">Creado</th>
              <th className="px-4 py-3">Último login</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-10 text-[#6B7280] font-mono text-xs">Cargando…</td></tr>}
            {!loading && users.map((u) => (
              <tr key={u.id} className="border-t border-[#EAECEA] hover:bg-[#F2F4ED]" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 text-sm text-[#1F2937] font-semibold">{u.full_name}</td>
                <td className="px-4 py-3 text-sm text-[#374151] font-mono">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="font-mono text-[10px] uppercase px-2 py-1 bg-accent text-[#1F2937] tracking-wider">
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-[#6B7280]">{format(parseISO(u.created_at), "d MMM yyyy", { locale: es })}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#6B7280]">{u.last_login_at ? format(parseISO(u.last_login_at), "d MMM · HH:mm", { locale: es }) : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`font-mono text-[10px] uppercase tracking-wider ${u.is_active ? "text-green-400" : "text-red-400"}`}>
                    {u.is_active ? "Activo" : "Inactivo"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit(u)} className="text-[#4B5563] hover:text-accent" aria-label="Editar">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(u)} className="text-[#6B7280] hover:text-red-400" aria-label="Borrar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showDialog && (
        <UserDialog
          user={editing}
          onClose={() => setShowDialog(false)}
          onSaved={() => { setShowDialog(false); load(); }}
        />
      )}
    </div>
  );
}

function UserDialog({ user, onClose, onSaved }) {
  const isEdit = Boolean(user);
  const [form, setForm] = useState({
    email: user?.email || "",
    full_name: user?.full_name || "",
    role: user?.role || "sales",
    is_active: user?.is_active ?? true,
    password: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEdit) {
        const payload = { full_name: form.full_name, role: form.role, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        await adminHttp.patch(`/admin/users/${user.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        if (!form.password || form.password.length < 8) { toast.error("Contraseña mínima 8 caracteres"); setLoading(false); return; }
        await adminHttp.post("/admin/users", form);
        toast.success("Usuario creado");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-accent/50 w-full max-w-md p-6 md:p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="user-dialog">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4B5563] hover:text-accent" aria-label="Cerrar">
          <X className="w-5 h-5" />
        </button>
        <h2 className="font-display font-black text-2xl mb-6">{isEdit ? "Editar usuario" : "Nuevo usuario"}</h2>
        <form onSubmit={submit} className="space-y-4">
          <Input label="Nombre completo" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required />
          <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} disabled={isEdit} required />
          <Input
            label={isEdit ? "Nueva contraseña (opcional)" : "Contraseña"}
            type="password"
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            required={!isEdit}
          />
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]">Rol</span>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-2 w-full bg-white border border-[#DDE2D5] px-3 py-3 text-[#1F2937] focus:outline-none focus:border-accent">
              <option value="sales">Ventas</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Activo
            </label>
          )}
          <button type="submit" disabled={loading} className="btn-accent w-full justify-center disabled:opacity-60" data-testid="user-dialog-submit">
            {loading ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear usuario"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, ...rest }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
        className="mt-2 w-full bg-white border border-[#DDE2D5] px-3 py-3 text-[#1F2937] focus:outline-none focus:border-accent disabled:opacity-50"
      />
    </label>
  );
}
